// File: src/hooks/usePermissions.js
// Hook for role-based permissions

import { useMemo } from 'react';

// What each role may do, per resource. Mirrors the middleware on the server -
// when one changes, change both.
//
// A country admin's map is identical to a super admin's on purpose: they may do
// all the same THINGS, to a smaller set of rows. Which rows is decided by the
// server from the country on their account, and the UI never sees the ones they
// may not have - so there is nothing here to hide.
const ROLE_PERMISSIONS = {
  super_admin: {
    series: ['view', 'create', 'edit', 'delete'],
    races: ['view', 'create', 'edit', 'delete'],
    drivers: ['view', 'create', 'edit', 'delete'],
    race_rankings: ['view', 'create', 'edit', 'delete'],
    events: ['view', 'create', 'edit', 'delete'],
    users: ['view', 'create', 'edit', 'delete']
  },
  country_admin: {
    series: ['view', 'create', 'edit', 'delete'],
    races: ['view', 'create', 'edit', 'delete'],
    drivers: ['view', 'create', 'edit', 'delete'],
    race_rankings: ['view', 'create', 'edit', 'delete'],
    events: ['view', 'create', 'edit', 'delete'],
    users: ['view', 'create', 'edit', 'delete']
  },
  editor: {
    series: ['view', 'create', 'edit', 'delete'],
    races: ['view', 'create', 'edit', 'delete'],
    drivers: ['view', 'create', 'edit', 'delete'],
    race_rankings: ['view', 'create', 'edit', 'delete'],
    events: ['view', 'create', 'edit', 'delete'],
    // User management is admin-only on the server, so claiming 'view' here
    // would only produce UI that 403s.
    users: []
  },
  organizer: {
    // Own events only - ownership is enforced server-side, not here.
    // 'view' is what lets a class be linked to a championship; it maps to the
    // narrow /api/series/options endpoint, not the full series payload.
    series: ['view'],
    races: [],
    drivers: [],
    race_rankings: [],
    events: ['view', 'create', 'edit', 'delete'],
    users: []
  },
  competitor: {
    series: [],
    races: [],
    drivers: [],
    race_rankings: [],
    events: [],
    users: []
  }
};

/**
 * Custom hook for checking user permissions based on their role
 * @param {string} userRole - 'super_admin', 'country_admin', 'editor',
 *                             'organizer' or 'competitor'
 * @returns {object} - Permission checker functions
 *
 * The server is the authority; these checks only decide what to render, so that
 * the UI doesn't offer actions that would come back 403.
 */
export const usePermissions = (userRole) => {
  const permissions = useMemo(() => {
    // 'admin' is the old name for super_admin, and a session started before the
    // rename still has it in localStorage.
    const named = userRole === 'admin' ? 'super_admin' : userRole;

    // Fail closed: an unrecognised role gets the least-privileged mapping.
    const role = ROLE_PERMISSIONS[named] ? named : 'competitor';

    const userPerms = ROLE_PERMISSIONS[role];

    return {
      // Check if user can perform an action on a specific table
      can: (action, table) => {
        return userPerms[table]?.includes(action) || false;
      },

      // Check if user can edit a specific table
      canEdit: (table) => {
        return userPerms[table]?.includes('edit') || false;
      },

      // Check if user can create in a specific table
      canCreate: (table) => {
        return userPerms[table]?.includes('create') || false;
      },

      // Check if user can delete from a specific table
      canDelete: (table) => {
        return userPerms[table]?.includes('delete') || false;
      },

      // Anyone who administers accounts. Both admin roles see the same screens;
      // the server decides whose accounts are on them.
      isAdmin: () => {
        return role === 'super_admin' || role === 'country_admin';
      },

      // Unrestricted: every country, and the only role that can appoint another
      // super admin.
      isSuperAdmin: () => {
        return role === 'super_admin';
      },

      // Confined to one country. Worth distinguishing so the UI can say so.
      isCountryAdmin: () => {
        return role === 'country_admin';
      },

      // Check if user is editor
      isEditor: () => {
        return role === 'editor';
      },

      // Get all editable tables for the user
      getEditableTables: () => {
        return Object.keys(userPerms).filter(table =>
          userPerms[table].includes('edit')
        );
      },

      // Check if user is an event organizer
      isOrganizer: () => {
        return role === 'organizer';
      },

      // Check if user is a competitor (self-registered account)
      isCompetitor: () => {
        return role === 'competitor';
      },

      // Anyone who may create and manage events
      canManageEvents: () => {
        return userPerms.events.includes('create');
      },

      // Organizers have no series/driver/results rights, so the series-centric
      // admin screens are meaningless for them.
      canManageSeries: () => {
        return userPerms.series.includes('edit');
      },

      // Get user role
      getRole: () => {
        return role;
      }
    };
  }, [userRole]);

  return permissions;
};

export default usePermissions;
